import Default from "layouts/auth/types/Default";
import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  verifyPhoneCode,
  resendPhoneCode,
  addPhoneNumber,
  getUserProfile,
} from "api";
import { normaliseToE164 } from "utils/phone";
import { useNavigate } from "react-router-dom";
import { debounce } from "lodash";
import { useAuth } from "contexts/authContext";
import { saveToken } from "utils/auth";
import { useQueryClient } from "@tanstack/react-query";

const LoadingSpinner = () => (
  <div className="flex h-screen items-center justify-center bg-white dark:bg-navy-900">
    <div className="h-16 w-16 animate-spin rounded-full border-4 border-dashed border-orange-500" />
  </div>
);

function PhoneVerificationDefault() {
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [phone, setPhone] = useState("");
  const [signupPhone, setSignupPhone] = useState("");
  const [selectedPhoneOption, setSelectedPhoneOption] = useState("signup");
  const [codeSent, setCodeSent] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [codeExpiry, setCodeExpiry] = useState(null);

  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const queryClient = useQueryClient();
  const codeInputs = useRef([]);
  const phoneInput = useRef(null);

  // Fetch user profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      setIsLoading(true);
      try {
        const profile = await getUserProfile();
        if (profile.user?.phoneVerified) {
          setSuccess("Your phone number is already verified!");
          setTimeout(() => navigate("/admin/dashboard", { state: { from: "phone-verification" } }), 1500);
        } else if (profile.user?.phoneNumber) {
          setSignupPhone(profile.user.phoneNumber);
          setPhone(profile.user.phoneNumber);
          setSelectedPhoneOption("signup");
        } else {
          setSelectedPhoneOption("new");
        }
      } catch (err) {
        setError("Failed to load profile. Please enter a phone number.");
        setSelectedPhoneOption("new");
        console.error("Failed to fetch user profile:", err);
      } finally {
        setIsLoading(false);
      }
    };
    fetchProfile();
  }, [navigate]);

  // Auto-focus phone input on mount
  useEffect(() => {
    if (phoneInput.current && !codeSent) {
      phoneInput.current.focus();
    }
  }, []);

  // Auto-focus first code input when code is sent
  useEffect(() => {
    if (codeSent && codeInputs.current[0]) {
      codeInputs.current[0].focus();
    }
  }, [codeSent]);

  // Clear error/success messages after 5 seconds
  useEffect(() => {
    if (error || success) {
      const timer = setTimeout(() => {
        setError("");
        setSuccess("");
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, success]);

  // Code expiry countdown
  useEffect(() => {
    if (codeSent && codeExpiry) {
      const timer = setInterval(() => {
        const timeLeft = Math.max(0, Math.floor((codeExpiry - Date.now()) / 1000));
        if (timeLeft === 0) {
          setCodeExpiry(null);
          setError("Verification code expired. Please resend a new code.");
        }
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [codeExpiry]);

  const handleSendCode = async () => {
    if (!phone || !/^\+[1-9]\d{1,14}$/.test(phone)) {
      setError("Please enter a valid phone number (e.g., +254741302824).");
      return;
    }
    setIsLoading(true);
    try {
      let response;
      if (selectedPhoneOption === "signup" && phone === signupPhone) {
        response = await resendPhoneCode();
      } else {
        response = await addPhoneNumber(phone);
        if (response.message?.includes("added")) setSignupPhone(phone);
      }

      if (
        response.message === "Verification code resent" ||
        response.message === "Verification code re-sent to existing number" ||
        response.message === "Phone number added; verification code sent"
      ) {
        setSuccess("Code sent to your phone!");
        setCodeSent(true);
        setResendAttempts((n) => n + 1);
        setCode(["", "", "", "", "", ""]);
        setCodeExpiry(Date.now() + 5 * 60 * 1000); // 5 minutes expiry
        await refreshUser();
      } else if (response.message === "Phone already verified") {
        setSuccess("Your phone number is already verified!");
        await refreshUser();
        setTimeout(() => navigate("/admin/dashboard", { state: { from: "phone-verification" } }), 1500);
      } else {
        setError("Failed to send code. Please try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to send code. Please try again.");
      console.error("Send code error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerificationClick = useCallback(
    debounce(async () => {
      const codeString = code.join("");
      if (!codeString || codeString.length !== 6 || !/^\d{6}$/.test(codeString)) {
        setError("Please enter a valid 6-digit code.");
        return;
      }
      setIsLoading(true);
      try {
        const response = await verifyPhoneCode(codeString);
        if (response.message === "Phone number verified successfully") {
          if (response.token) saveToken(response.token, true);
          await refreshUser();
          queryClient.invalidateQueries(["userProfile"]);
          setSuccess("Verification successful! Redirecting...");
          setTimeout(() => navigate("/admin/dashboard", { state: { from: "phone-verification" } }), 800);
        } else {
          setError("Verification failed. Please check the code.");
        }
      } catch (err) {
        setError(err.response?.data?.message || "Verification failed. Please try again.");
        console.error("verifyPhoneCode error:", err);
      } finally {
        setIsLoading(false);
      }
    }, 200),
    [code, navigate, queryClient, refreshUser]
  );

  const handleResendCode = async () => {
    if (resendAttempts >= 3) {
      setError("Maximum resend attempts reached. Please contact support.");
      return;
    }
    setIsLoading(true);
    try {
      const response = await resendPhoneCode();
      if (response.message === "Verification code resent") {
        setSuccess("New code sent to your phone!");
        setResendAttempts((n) => n + 1);
        setCode(["", "", "", "", "", ""]);
        setCodeExpiry(Date.now() + 5 * 60 * 1000);
        await refreshUser();
      } else if (response.message === "Phone already verified") {
        setSuccess("Your phone number is already verified!");
        await refreshUser();
        setTimeout(() => navigate("/admin/dashboard", { state: { from: "phone-verification" } }), 1500);
      } else {
        setError("Failed to resend code. Try again.");
      }
    } catch (err) {
      setError(err.response?.data?.message || "Failed to resend code. Please contact support.");
      console.error("resendPhoneCode error:", err);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneChange = (val) => {
    setError("");
    try {
      const normalized = normaliseToE164(val);
      setPhone(normalized);
    } catch (err) {
      setPhone(val.startsWith("+") ? val : "+" + val.replace(/^\+/, ""));
    }
  };

  const handleCodeChange = (value, index) => {
    if (!/^\d?$/.test(value)) return;
    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");
    if (value && index < 5) {
      codeInputs.current[index + 1].focus();
    }
  };

  const handleCodeKeyDown = (e, index) => {
    if (e.key === "Backspace" && !code[index] && index > 0) {
      codeInputs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      const newCode = pasted.split("").slice(0, 6);
      setCode(newCode);
      codeInputs.current[5].focus();
    }
  };

  const formatTimeLeft = () => {
    if (!codeExpiry) return "";
    const seconds = Math.max(0, Math.floor((codeExpiry - Date.now()) / 1000));
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}:${secs < 10 ? "0" : ""}${secs}`;
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <Default
      maincard={
        <div className="flex flex-col w-full max-w-[440px] px-4 md:px-0 mt-[10vh] z-10">
          <div className="flex flex-col bg-white dark:bg-navy-900 rounded-3xl p-10 shadow-md transition-all duration-300">
            <h2 className="text-4xl font-bold text-navy-700 dark:text-white">
              Two-Step Verification
            </h2>
            <p className="mt-4 text-base text-gray-600 dark:text-gray-400">
              {codeSent
                ? `Enter the 6-digit code sent to ${phone}.`
                : "Add a phone number to secure your account."}
            </p>

            {/* Alerts */}
            {error && (
              <div className="mt-6 flex items-center rounded-lg bg-red-50 dark:bg-red-900/20 p-3 text-red-600 dark:text-red-400">
                <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm-1-5h2v2H9v-2zm0-6h2v4H9V7z" clipRule="evenodd" />
                </svg>
                <span className="text-base">{error}</span>
              </div>
            )}
            {success && (
              <div className="mt-6 flex items-center rounded-lg bg-green-50 dark:bg-green-900/20 p-3 text-green-600 dark:text-green-400">
                <svg className="mr-2 h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <span className="text-base">{success}</span>
              </div>
            )}

            {!codeSent && (
              <div className="mt-8 flex flex-col gap-6">
                {signupPhone && (
                  <label className="flex items-center gap-3 text-base text-gray-600 dark:text-gray-400">
                    <input
                      type="radio"
                      name="phoneOption"
                      value="signup"
                      checked={selectedPhoneOption === "signup"}
                      onChange={() => {
                        setSelectedPhoneOption("signup");
                        setPhone(signupPhone);
                      }}
                      className="h-5 w-5 text-orange-500 focus:ring-orange-500"
                      aria-label="Use registered phone number"
                    />
                    Use registered phone: {signupPhone}
                  </label>
                )}
                <label className="flex items-center gap-3 text-base text-gray-600 dark:text-gray-400">
                  <input
                    type="radio"
                    name="phoneOption"
                    value="new"
                    checked={selectedPhoneOption === "new"}
                    onChange={() => {
                      setSelectedPhoneOption("new");
                      setPhone("");
                    }}
                    className="h-5 w-5 text-orange-500 focus:ring-orange-500"
                    aria-label="Use a new phone number"
                  />
                  Use a new phone number
                </label>
                {selectedPhoneOption === "new" && (
                  <input
                    type="tel"
                    placeholder="Enter phone number (e.g., +254741302824)"
                    value={phone}
                    onChange={(e) => handlePhoneChange(e.target.value)}
                    ref={phoneInput}
                    className="w-full h-12 rounded-lg border border-gray-200 p-3 text-base text-navy-700 dark:border-navy-700 dark:bg-navy-900 dark:text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                    aria-label="Phone number"
                  />
                )}
                <button
                  onClick={handleSendCode}
                  disabled={isLoading || !phone || !/^\+[1-9]\d{1,14}$/.test(phone)}
                  className="mt-4 w-full h-12 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium hover:from-orange-600 hover:to-orange-700 dark:from-orange-400 dark:to-orange-500 dark:hover:from-orange-500 dark:hover:to-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-200"
                >
                  {isLoading ? "Sending..." : "Send Verification Code"}
                </button>
              </div>
            )}

            {codeSent && (
              <div className="mt-8 flex flex-col items-center gap-6">
                <div className="flex gap-4">
                  {code.map((digit, index) => (
                    <input
                      key={index}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(e.target.value, index)}
                      onKeyDown={(e) => handleCodeKeyDown(e, index)}
                      onPaste={handlePaste}
                      ref={(el) => (codeInputs.current[index] = el)}
                      className="h-12 w-12 rounded-lg border border-gray-200 text-center text-xl text-navy-700 dark:border-navy-700 dark:bg-navy-900 dark:text-white focus:border-orange-500 focus:ring-1 focus:ring-orange-500"
                      aria-label={`Code digit ${index + 1}`}
                      disabled={isLoading}
                    />
                  ))}
                </div>
                {codeExpiry && (
                  <p className="text-base text-gray-600 dark:text-gray-400">
                    Code expires in: <span className="font-medium">{formatTimeLeft()}</span>
                  </p>
                )}
                <button
                  onClick={handleVerificationClick}
                  disabled={isLoading || code.join("").length !== 6}
                  className="w-full h-12 rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white font-medium hover:from-orange-600 hover:to-orange-700 dark:from-orange-400 dark:to-orange-500 dark:hover:from-orange-500 dark:hover:to-orange-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition duration-200"
                >
                  {isLoading ? "Verifying..." : "Verify Code"}
                </button>
                <p className="text-base text-gray-600 dark:text-gray-400">
                  Didn’t get the code?{" "}
                  <button
                    onClick={handleResendCode}
                    disabled={resendAttempts >= 3 || isLoading}
                    className="text-orange-500 hover:underline disabled:text-orange-300"
                  >
                    Resend ({3 - resendAttempts} attempts left)
                  </button>{" "}
                  or{" "}
                  <a href="mailto:support@iforge.io" className="text-orange-500 hover:underline">
                    contact support
                  </a>.
                </p>
              </div>
            )}
          </div>
        </div>
      }
    />
  );
}

export default PhoneVerificationDefault;